/**
 * Authentication for the console.
 *
 * Named "shared" while it lived at the repository root, which it never was:
 * only this application ever imported it. The landing page has its own
 * session handling and the backend has none of this at all.
 */

class SharedAuth {
  constructor() {
    this.tokenKey = 'icepop_auth_token';
    this.userKey = 'icepop_user_data';
    // Use import.meta.env for Vite, fallback to default for Node.js
    try {
      this.apiBaseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001';
    } catch (e) {
      this.apiBaseUrl = 'http://localhost:3001';
    }
  }

  // Get stored token
  getToken() {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(this.tokenKey);
    }
    return null;
  }

  // Set token in storage
  setToken(token) {
    if (typeof window === 'undefined') return;

    // Everything read back out of localStorage is treated as trusted by the
    // rest of the app, so storing whatever a caller passes — undefined, an
    // object, a stringified error — puts a value into that trusted position
    // that was never a token. A JWT is three dot-separated base64url segments
    // and nothing else.
    //
    // The stored value is derived from the input rather than being the input
    // that a check happened to approve. Shape-checking `token` and then
    // storing `token` leaves the raw login response reaching storage
    // untouched: the guard decides whether to proceed but transforms nothing,
    // so neither a reader nor a dataflow analysis can see a constraint
    // travelling with the value. That is what kept this open through the
    // passes that hardened setUser below — those went through replace(), this
    // did not.
    const jwt = typeof token === 'string' ? token.replace(/[^\w.-]/g, '') : '';

    // Requiring the strip to have changed nothing keeps the original
    // strictness. A token needing repair was never a valid JWT, and quietly
    // storing a cleaned-up one would hide the real fault behind a 401 later.
    if (jwt !== token || !/^[\w-]+\.[\w-]+\.[\w-]+$/.test(jwt)) {
      console.error('SharedAuth: refusing to store a value that is not a JWT');
      return;
    }

    localStorage.setItem(this.tokenKey, jwt);
  }

  // Remove token from storage
  removeToken() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.userKey);
    }
  }

  // Get user data
  getUser() {
    if (typeof window === 'undefined') return null;

    const userData = localStorage.getItem(this.userKey);
    if (!userData) return null;

    try {
      const parsed = JSON.parse(userData);

      // Checked on the way out as well as in. localStorage is writable by
      // anything running on the page, and hasRole() reads `role` straight off
      // this object to decide what the UI offers. That is a convenience, not a
      // control — the server re-checks the JWT — but a malformed entry should
      // log the session out rather than flow on as a half-built user.
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || typeof parsed.role !== 'string') {
        localStorage.removeItem(this.userKey);
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem(this.userKey);
      return null;
    }
  }

  // Set user data
  setUser(user) {
    if (typeof window === 'undefined') return;

    if (!user || typeof user !== 'object' || Array.isArray(user)) {
      console.error('SharedAuth: refusing to store a user that is not an object');
      return;
    }

    // Rebuilt from the five fields the UI actually reads rather than storing
    // the login response whole. Two reasons: whatever else the API returns
    // now or later — internal ids, flags, a password hash added by mistake —
    // never reaches localStorage, and what does reach it is a fresh object of
    // known primitives rather than an arbitrary body echoed into storage.
    //
    // Each value is then reduced to the characters its field can legitimately
    // hold, at the assignment rather than in a shared helper. Three earlier
    // passes at this put the same checks inside an `asText` function and the
    // finding stayed open through all of them: taint analysis does not follow
    // the call, so from outside, an API response still appeared to land in
    // storage untouched. Inlining costs a few similar-looking lines and buys a
    // check that is visible where it happens — to a reader as much as to a
    // scanner.
    //
    // Allowlists rather than a control-character denylist, because the set a
    // field should contain is easier to be sure of than the set it should not.
    // Markup characters, quotes and control characters are all absent by
    // construction, so nothing here can carry a payload into whatever renders
    // it later. Lengths are capped so a response padded to a megabyte cannot
    // spend this origin's storage quota.
    //
    // Only strings are taken. Anything else becomes '' and then undefined,
    // rather than being coerced into the string "[object Object]".
    const id = (typeof user.id === 'string' && user.id)
      || (typeof user._id === 'string' && user._id)
      || '';
    const username = typeof user.username === 'string' ? user.username : '';
    const email = typeof user.email === 'string' ? user.email : '';
    const role = typeof user.role === 'string' ? user.role : '';
    const referralLink = typeof user.referralLink === 'string' ? user.referralLink : '';

    // Cleaned first, then required to be a web URL. A character allowlist
    // alone is not enough here: `javascript:alert(1)` is made entirely of
    // characters a URL may legitimately contain, so it would pass through
    // untouched, and this value is rendered and offered for copying.
    const link = referralLink.replace(/[^\w:/?#[\]@!$&'()*+,;=.~%-]/g, '').slice(0, 256);

    localStorage.setItem(this.userKey, JSON.stringify({
      // Opaque handles: ObjectIds are hex, UUIDs add hyphens.
      id: id.replace(/[^\w-]/g, '').slice(0, 256) || undefined,
      // Letters in any script, so an accented name survives being stored.
      username: username.replace(/[^\p{L}\p{N} ._@+-]/gu, '').trim().slice(0, 256) || undefined,
      // The apostrophe is allowed deliberately: o'brien@example.com is a real
      // address, and silently storing a different one is its own bug.
      email: email.replace(/[^\w.@+'-]/g, '').slice(0, 256) || undefined,
      // Every role this system issues is lower-case words joined by
      // underscores. Not an allowlist of the four that exist today —
      // getUser() drops a session whose role it cannot read, so a role added
      // server-side and forgotten here would log those users out with no clue
      // why. Defence in depth either way: the server re-checks the JWT, and
      // what is stored here only decides what the UI offers.
      role: role.replace(/[^a-z_]/g, '').slice(0, 32) || undefined,
      referralLink: /^https?:\/\/\w/.test(link) ? link : undefined,
    }));
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    return !!token;
  }

  // Check if user has specific role
  hasRole(role) {
    const user = this.getUser();
    return user && user.role === role;
  }

  // Check if user is admin
  isAdmin() {
    return this.hasRole('admin') || this.hasRole('super_admin');
  }

  // Check if user is super admin
  isSuperAdmin() {
    return this.hasRole('super_admin');
  }

  // Login function
  async login(username, password) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      console.log('SharedAuth login - response data:', data); // Debug log

      if (response.ok && data.success) {
        console.log('SharedAuth login - setting user:', data.admin); // Debug log
        this.setToken(data.token);
        this.setUser(data.admin);
        return { success: true, user: data.admin };
      } else {
        console.log('SharedAuth login - failed:', data); // Debug log
        return { success: false, error: data.message || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  // Logout function
  logout() {
    this.removeToken();
    // Redirect to appropriate login page based on current app
    if (typeof window !== 'undefined') {
      const currentHost = window.location.hostname;
      const currentPort = window.location.port;
      
      if (currentPort === '5175' || currentHost.includes('admin')) {
        // The console at /superadmin, which owns the login form.
        //
        // This used to send people to /admin, which was a second dashboard with
        // its own login. That page is gone and /admin now redirects to
        // /superadmin, so logging out here bounced straight back into the app —
        // and if the app then made a request that 401'd, it logged out again.
        // A redirect loop between the two paths, from one stale target.
        window.location.href = '/superadmin';
      } else {
        // Landing page - redirect to home
        window.location.href = '/';
      }
    }
  }

  // Verify token with backend
  async verifyToken() {
    const token = this.getToken();
    if (!token) {
      return { success: false, error: 'No token found' };
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/api/admin/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log('SharedAuth verifyToken - response data:', data); // Debug log

      if (response.ok && data.success) {
        console.log('SharedAuth verifyToken - setting user:', data.admin); // Debug log
        this.setUser(data.admin);
        return { success: true, user: data.admin };
      } else {
        console.log('SharedAuth verifyToken - invalid response:', data); // Debug log
        this.removeToken();
        return { success: false, error: 'Invalid token' };
      }
    } catch (error) {
      console.log('SharedAuth verifyToken - error:', error); // Debug log
      return { success: false, error: 'Network error' };
    }
  }

  // Get auth headers for API requests
  getAuthHeaders() {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // Check if token is expired (basic check)
  isTokenExpired() {
    const token = this.getToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }
}

// Create singleton instance
const sharedAuth = new SharedAuth();

// Export for different module systems
if (typeof window !== 'undefined') {
  window.sharedAuth = sharedAuth;
}

export default sharedAuth; 